import { Divider, Form } from 'antd';
import ButtonsToGenerateReports from '@/components/Reports/ButtonsToGenerateReports';
import { DefaultRouteTabs } from '@/components/_common';
import { PageProps } from '@/types/PageProps';
import CustomerWithoutMovementForm from '@/components/Reports/MovementReports/CustomerWithoutMovementForm';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';
import {
  QuestCustomerWithoutMovementFormValues,
  QuestCustomerWithoutMovementRequest,
} from '@/core/domain/questReport/Reports/CustomerWithoutMovement/CustomerWithoutMovement.types';
import useFileLoading, { FileType } from '@/hooks/useFileLoading';
import useReportGenerate, { ReportCallback } from '@/hooks/useReportGenerate';
import { DateTimeFormat } from '@/utils/moment';
import { useCustomerWithoutMovementReport } from '@/core/domain/questReport/Reports/CustomerWithoutMovement/CustomerWithoutMovement.hook';
import { QuestResponseType } from '@/core/domain/questReport/Reports/Quotation/Quotation.types';

export default function MovementCustomerWithoutMovementPage({
  route,
}: PageProps) {
  const intl = useIntl();
  const [form] = Form.useForm<QuestCustomerWithoutMovementFormValues>();

  const { setFileLoading, setFileUnloading, fileLoading } = useFileLoading();
  const report = useReportGenerate();

  const generateReport = useCustomerWithoutMovementReport();

  const handleGenerateReport = async (
    format: FileType,
    callback?: ReportCallback,
  ) => {
    setFileLoading(format);

    try {
      const { companiesIds, initialDate, finalDate, ...values } =
        await form.validateFields();

      const obj: QuestCustomerWithoutMovementRequest = {
        ...values,
        responseType:
          format === 'CSV' ? QuestResponseType.CSV : QuestResponseType.PDF,
        companiesIds: companiesIds?.toString(),
        startDate: initialDate.format(DateTimeFormat),
        endDate: finalDate.format(DateTimeFormat),
      };

      const result = await generateReport.mutateAsync(obj);

      if (callback) {
        callback(result);
      }
    } catch {}

    setFileUnloading(format);
  };

  const handleView = () => {
    handleGenerateReport('REPORT_VIEW', (result) =>
      report.openFile({
        result,
        fileName: 'relatorio_cliente_sem_movimentacao.pdf',
        fileType: 'application/pdf',
      }),
    );
  };

  const handlePdf = () => {
    handleGenerateReport('PDF', (result) =>
      report.downloadFile({
        result,
        fileName: 'relatorio_cliente_sem_movimentacao.pdf',
      }),
    );
  };

  const handleCsv = () => {
    handleGenerateReport('CSV', (result) =>
      report.downloadFile({
        result,
        fileName: 'relatorio_cliente_sem_movimentacao.csv',
      }),
    );
  };

  return (
    <DefaultRouteTabs route={route.parent} permissions={route.permissions}>
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <CustomerWithoutMovementForm form={form} />

      <Divider />

      <ButtonsToGenerateReports
        viewReport={handleView}
        generatePdf={handlePdf}
        generateCsv={handleCsv}
        loading={fileLoading}
      />
    </DefaultRouteTabs>
  );
}
